// components/admin/AdminChatView.jsx
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ArrowLeft, Send, Loader, User, ShoppingBag } from 'lucide-react';
import { socket } from '../chat/socket';

const API_URL = process.env.REACT_APP_API_URL;

function AdminChatView({ sessionId, userId, onBack }) {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadChatData();
    
    // Join Socket.IO room for this chat
    const room = `chat_${sessionId}`;
    socket.emit('admin_join_chat', room);
    
    console.log(`[Admin Chat] Joined room: ${room}`);
    
    // Listen for new messages
    const handleNewMessage = (data) => {
      console.log('[Admin Chat] New message received:', data);
      if (data.session_id === sessionId) {
        setMessages(prev => [...prev, data.message]);
      }
    };
    
    socket.on('new_message', handleNewMessage);
    
    // Cleanup
    return () => {
      socket.emit('admin_leave_chat', room);
      socket.off('new_message', handleNewMessage);
      console.log(`[Admin Chat] Left room: ${room}`);
    };
  }, [sessionId]);

  const loadChatData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_URL}/api/admin/chat/${sessionId}/messages?user_id=${userId}`
      );
      
      setSession(response.data.session);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Error loading chat data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim()) return;
    
    const messageText = inputMessage.trim();
    setInputMessage('');
    setIsSending(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/chat/${sessionId}/send`,
        {
          user_id: userId,
          message: messageText
        }
      );
      
      // Message will be added via Socket.IO event
      console.log('[Admin Chat] Message sent successfully');
      
    } catch (error) {
      console.error('Error sending message:', error);
      // Re-add message to input on error
      setInputMessage(messageText);
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="w-12 h-12 text-brand-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Cargando chat...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">Chat no encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            
            <div className="flex-1">
              <h2 className="font-semibold text-gray-800">
                {session.buyer_details?.name || 'Cliente Anónimo'}
              </h2>
              <p className="text-sm text-gray-600">
                Tienda: {session.seller?.username || 'Desconocida'}
              </p>
            </div>
          </div>
        </div>

        {/* Buyer Details Card (if completed) */}
        {session.buyer_details && (
          <div className="px-4 pb-3">
            <div className="bg-brand-green/5 border border-brand-green/20 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-brand-green mb-2">
                Información del Cliente
              </h3>
              <div className="space-y-1 text-sm text-gray-700">
                <p><span className="font-semibold">Nombre:</span> {session.buyer_details.name}</p>
                <p><span className="font-semibold">WhatsApp:</span> {session.buyer_details.phone}</p>
                <p><span className="font-semibold">Dirección:</span> {session.buyer_details.address}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.map((msg) => (
            <ChatMessageBubble key={msg.id} message={msg} />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t shadow-lg">
        <div className="max-w-4xl mx-auto p-4">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Escribe un mensaje como administrador..."
              disabled={isSending}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:bg-gray-100"
            />
            <button
              type="submit"
              disabled={isSending || !inputMessage.trim()}
              className="bg-brand-primary text-white p-3 rounded-full hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isSending ? (
                <Loader className="w-6 h-6 animate-spin" />
              ) : (
                <Send className="w-6 h-6" />
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ChatMessageBubble({ message }) {
  const getSenderInfo = () => {
    switch (message.sender) {
      case 'buyer':
        return {
          align: 'right',
          bg: 'bg-blue-500',
          textColor: 'text-white',
          icon: <User className="w-4 h-4" />,
          label: 'Cliente'
        };
      case 'bot':
        return {
          align: 'left',
          bg: 'bg-white',
          textColor: 'text-gray-800',
          icon: <ShoppingBag className="w-4 h-4" />,
          label: 'Bot'
        };
      case 'admin':
        return {
          align: 'left',
          bg: 'bg-brand-primary',
          textColor: 'text-white',
          icon: <User className="w-4 h-4" />,
          label: 'Admin'
        };
      default:
        return {
          align: 'left',
          bg: 'bg-gray-300',
          textColor: 'text-gray-800',
          icon: null,
          label: 'Sistema'
        };
    }
  };

  const senderInfo = getSenderInfo();

  return (
    <div className={`flex ${senderInfo.align === 'right' ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[70%]">
        <div className="flex items-center gap-2 mb-1 px-1">
          {senderInfo.icon}
          <span className="text-xs font-semibold text-gray-600">
            {senderInfo.label}
          </span>
        </div>
        <div
          className={`rounded-2xl px-4 py-3 shadow-sm ${
            senderInfo.align === 'right' ? 'rounded-br-none' : 'rounded-bl-none'
          } ${senderInfo.bg} ${senderInfo.textColor}`}
        >
          <p className="text-sm whitespace-pre-wrap break-words">
            {message.content}
          </p>
          <p
            className={`text-xs mt-1 ${
              senderInfo.textColor === 'text-white' ? 'text-white/70' : 'text-gray-500'
            }`}
          >
            {new Date(message.timestamp).toLocaleTimeString('es-PE', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
      </div>
    </div>
  );
}

export default AdminChatView;